import { defineStore } from 'pinia';
import { Network } from '@capacitor/network';
import { uid } from '@/app/utils/uniqid';

import { CreateFolder } from '@/app/form/models/CreateFolder';
import folderClient from '../api/folder.client';

interface FolderState {
  folders: CreateFolder[];
}

export const useFolderStore = defineStore('folders', {
  state: (): FolderState => ({
    folders: [],
  }),
  getters: {
    getFolder: (state) => (uuid: string) =>
      state.folders.find((f) => f.uuid === uuid),
  },
  actions: {
    async sendFolder(folder: CreateFolder) {
      if (folder.send) {
        return;
      }

      // Checkpoints folder.submissionProgress after every successfully
      // completed unit of work, so a retry (e.g. triggered by
      // retryPendingFolders on reconnect) only sends what's still missing.
      const persist = () => this.updateFolder(folder);

      try {
        await folderClient.create(folder, persist);
        folder.send = true;
        folder.sendPending = false;
        persist();

        this.folders.splice(
          this.folders.findIndex((f) => f.uuid === folder.uuid),
          1
        );
      } catch (error) {
        const status = await Network.getStatus();
        folder.sendPending = !status.connected;
        persist();

        throw error;
      }
    },
    // Retries every draft that previously failed to send while offline —
    // meant to be called once the app detects the connection came back.
    async retryPendingFolders() {
      const pending = this.folders.filter((f) => f.sendPending);

      for (const folder of pending) {
        try {
          await this.sendFolder(folder);
        } catch {
          // Still failing (offline again, or a real error) — it stays in
          // the list as sendPending and will be retried on the next
          // reconnect or manual attempt.
        }
      }
    },
    createFolder(folder: CreateFolder) {
      if (folder.uuid) {
        this.updateFolder(folder);
        return;
      }

      this.folders.push(folder);
    },
    updateFolder(folder: CreateFolder) {
      if (!folder.uuid) {
        folder.uuid = uid();
        this.folders.push(folder);

        return;
      }

      const f = this.folders.find((f) => f.uuid === folder.uuid);
      if (!f) {
        return;
      }

      Object.assign(f, folder);
    },
    deleteFolder(uuid: string) {
      const idx = this.folders.findIndex((f) => f.uuid === uuid);
      if (idx === -1) {
        return;
      }

      this.folders.splice(idx, 1);
    },
  },
  persist: {
    enabled: true,
  },
});
